import { useEffect, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { isMobile } from 'react-device-detect'
import { generateRandomId, getVideoDevices } from '../helpers/WebcamHelper'
import cameraStreamManager from '../helpers/cameraStreamManager'
import { fileIdentifier, isRecording, setLastStatus, setStartedAt, stopRecording as stopRecordingAction } from '../redux/returnVideoRecorderSlice'
import VideoUploadClient from '../helpers/videoUploadClient'
import { RETURN_PROOF_CONFIG } from '../services/returnProofService'
import returnCameraEventQueue from '../helpers/returnCameraEventQueue'
import { CAMERA_STATUS, CAMERA_STOP_REASON } from '../helpers/cameraStatusConstants'

const VIDEO_MIME = 'video/webm'
const VIDEO_CODECS = 'vp9'
const VIDEO_TIMESLICE = 2000
const VIDEO_MAXIMUM_RECORDED_SECONDS = 600

// fileKey is `return_<return_id>_<random>` — the id is the second underscore segment.
const parseReturnIdFromFileKey = fileKey => {
  if (!fileKey) return null
  const parts = fileKey.split('_')
  const parsed = parseInt(parts[1], 10)
  return Number.isNaN(parsed) ? null : parsed
}

const useReturnVideoRecorder = () => {
  const dispatch = useDispatch()
  const shouldRecord = useSelector(isRecording)
  const fileKey = useSelector(fileIdentifier)
  // Kept in sync each render so long-lived closures (the unmount teardown)
  // read the current fileKey instead of the value captured at mount.
  const fileKeyRef = useRef(fileKey)
  fileKeyRef.current = fileKey

  // One object per recording session (see useWebcamRecorder): handlers close
  // over their own session so a late finalization can never touch the state
  // of the session that replaced it.
  const activeSession = useRef(null)
  const [isEnabled, setIsEnabled] = useState(null)
  const stopReason = useRef(CAMERA_STOP_REASON.MANUAL)

  const recorderIdRef = useRef(null)
  if (recorderIdRef.current === null) {
    recorderIdRef.current = `return_recorder_${generateRandomId()}`
  }

  const buildEventEmitter =
    sessionFileKey =>
    (status, context = null) => {
      const returnId = parseReturnIdFromFileKey(sessionFileKey)
      if (returnId === null) {
        console.warn('[useReturnVideoRecorder] skipping camera event: no return_id available', { status })
        return
      }
      returnCameraEventQueue.enqueue({
        return_id: returnId,
        file_identifier: sessionFileKey || null,
        status,
        occurred_at: new Date().toISOString(),
        context: {
          user_agent: navigator.userAgent,
          ...(context || {}),
        },
      })
      dispatch(setLastStatus(status))
    }

  const emitEvent = (status, context = null) => buildEventEmitter(fileKeyRef.current)(status, context)

  const releaseStream = session => {
    if (session.isSubscribed) {
      cameraStreamManager.release(session.subscriberId)
      session.isSubscribed = false
    }
    session.stream = null
  }

  const detectRecordingEnabled = async () => {
    if (isMobile) {
      setIsEnabled(false)
      return
    }
    const videoDevices = await getVideoDevices()
    setIsEnabled(videoDevices.length > 0)
  }

  useEffect(() => {
    detectRecordingEnabled()
  }, [])

  useEffect(() => {
    if (shouldRecord === null || shouldRecord === undefined) return
    if (isEnabled === null) return
    if (isEnabled === false) {
      if (shouldRecord) {
        emitEvent(CAMERA_STATUS.NO_DEVICE, { is_mobile: !!isMobile })
        dispatch(stopRecordingAction())
      }
      return
    }
    if (shouldRecord) {
      startRecording()
    } else {
      stopRecording()
    }
  }, [shouldRecord, isEnabled])

  // Scoped to the receive screen: on unmount, tear the recorder down so the video
  // is finalized and the camera stream released even if the operator navigates away.
  useEffect(() => {
    return () => {
      if (activeSession.current != null) {
        stopReason.current = CAMERA_STOP_REASON.ROUTE_CHANGE
        stopRecording()
      }
    }
  }, [])

  const startRecording = async () => {
    if (activeSession.current != null) {
      return
    }

    const session = {
      fileKey,
      emit: buildEventEmitter(fileKey),
      // Unique per session: a stale session releasing its subscription must
      // never evict the session that replaced it under a shared id.
      subscriberId: `${recorderIdRef.current}_${generateRandomId()}`,
      uploadClient: null,
      recorder: null,
      stream: null,
      isSubscribed: false,
      startTime: null,
      stopRequested: false,
    }
    activeSession.current = session

    const onStreamChange = (stream, info) => {
      if (info.reason === 'ended' && activeSession.current === session && !session.stopRequested) {
        session.emit(CAMERA_STATUS.DEVICE_UNAVAILABLE, { reason: 'track_ended' })
        dispatch(stopRecordingAction())
      }
    }

    try {
      session.stream = await cameraStreamManager.subscribe(session.subscriberId, onStreamChange)
      session.isSubscribed = true
    } catch (error) {
      // subscribe() registers the id before acquiring; drop the registration
      // so a failed session cannot keep the camera held open forever.
      cameraStreamManager.release(session.subscriberId)
      const status =
        error?.name === 'NotAllowedError'
          ? CAMERA_STATUS.PERMISSION_DENIED
          : error?.name === 'NotReadableError'
            ? CAMERA_STATUS.DEVICE_UNAVAILABLE
            : CAMERA_STATUS.GET_USER_MEDIA_ERROR
      session.emit(status, { error_name: error?.name || null, error_message: error?.message || null })
      activeSession.current = null
      dispatch(stopRecordingAction())
      return
    }

    if (session.stopRequested) {
      releaseStream(session)
      return
    }

    const uploadClient = new VideoUploadClient(RETURN_PROOF_CONFIG, {
      onChunkUploadFailed: info => session.emit(CAMERA_STATUS.UPLOAD_CHUNK_FAILED, info),
      onUploadCompleted: info => session.emit(CAMERA_STATUS.UPLOAD_COMPLETED, info),
      onUploadFailed: info => session.emit(CAMERA_STATUS.UPLOAD_FAILED, info),
    })
    session.uploadClient = uploadClient

    try {
      await uploadClient.createMultipartUpload(session.fileKey, 5)
    } catch (error) {
      session.emit(CAMERA_STATUS.UPLOAD_FAILED, {
        reason: 'create_multipart_upload_failed',
        error_name: error?.name || null,
        error_message: error?.message || null,
      })
      releaseStream(session)
      if (activeSession.current === session) {
        activeSession.current = null
        dispatch(stopRecordingAction())
      }
      return
    }

    if (session.stopRequested) {
      // Stop won the race against startup: nothing recorded. finish() resolves
      // without completing (empty upload) and the backend sweeper aborts it.
      uploadClient.finishUploadingAndCompleteMultipartUpload()
      releaseStream(session)
      return
    }

    const onRecordingDataAvailable = async event => {
      if (event.data?.size > 0) {
        try {
          await uploadClient.appendVideoChunk(event.data)
        } catch (error) {
          // Already reported through onChunkUploadFailed; the chunk cache is
          // kept, so the next append or the final flush retries this part.
        }
      }

      const currentRecordedSeconds = session.startTime ? (new Date() - session.startTime) / 1000 : 0
      if (!session.stopRequested && currentRecordedSeconds > VIDEO_MAXIMUM_RECORDED_SECONDS) {
        stopReason.current = CAMERA_STOP_REASON.MAX_DURATION
        dispatch(stopRecordingAction())
      }
    }

    // Fires after the recorder has flushed its final dataavailable event, so
    // by now every chunk is in the upload client: finalizing here is what
    // guarantees the video is completed with its full tail.
    const onRecordingStop = async () => {
      try {
        await uploadClient.finishUploadingAndCompleteMultipartUpload()
      } catch (error) {
        session.emit(CAMERA_STATUS.UPLOAD_FAILED, {
          reason: 'finish_upload_threw',
          error_name: error?.name || null,
          error_message: error?.message || null,
        })
      }
    }

    const onRecordingError = event => {
      session.emit(CAMERA_STATUS.RECORDING_ERROR, {
        error_name: event?.error?.name || null,
        error_message: event?.error?.message || null,
      })
    }

    try {
      session.recorder = new MediaRecorder(session.stream, {
        mimeType: `${VIDEO_MIME}; codecs=${VIDEO_CODECS}`,
        audioBitsPerSecond: 0,
      })
      session.recorder.addEventListener('dataavailable', onRecordingDataAvailable)
      session.recorder.addEventListener('stop', onRecordingStop)
      session.recorder.addEventListener('error', onRecordingError)
      session.recorder.start(VIDEO_TIMESLICE)
      session.startTime = new Date()
      dispatch(setStartedAt(session.startTime.toISOString()))
      session.emit(CAMERA_STATUS.RECORDING_STARTED, {
        mime_type: `${VIDEO_MIME}; codecs=${VIDEO_CODECS}`,
        timeslice_ms: VIDEO_TIMESLICE,
      })
    } catch (error) {
      session.emit(CAMERA_STATUS.RECORDING_ERROR, {
        error_name: error?.name || null,
        error_message: error?.message || null,
      })
      uploadClient.finishUploadingAndCompleteMultipartUpload()
      releaseStream(session)
      if (activeSession.current === session) {
        activeSession.current = null
        dispatch(stopRecordingAction())
      }
    }
  }

  const stopRecording = () => {
    const session = activeSession.current
    if (session == null) {
      return
    }
    activeSession.current = null
    session.stopRequested = true

    const reason = stopReason.current
    const durationSeconds = session.startTime ? (new Date() - session.startTime) / 1000 : 0

    if (session.recorder != null && session.recorder.state !== 'inactive') {
      // Listeners stay attached on purpose: stop() flushes one last
      // dataavailable and then fires stop, which finalizes the upload.
      session.recorder.stop()
    }
    // No recorder yet means stop raced startup: the stopRequested checkpoints
    // inside startRecording finalize the upload and release the stream.
    releaseStream(session)

    // Only sessions that actually recorded emit a stopped event, so telemetry
    // keeps pairing every stopped event with a recording_started one.
    if (session.startTime != null) {
      if (reason === CAMERA_STOP_REASON.MAX_DURATION) {
        session.emit(CAMERA_STATUS.RECORDING_STOPPED_TIMEOUT, { duration_seconds: durationSeconds })
      } else {
        session.emit(CAMERA_STATUS.RECORDING_STOPPED_NORMAL, { duration_seconds: durationSeconds, reason })
      }
    }
    stopReason.current = CAMERA_STOP_REASON.MANUAL
  }
}

export default useReturnVideoRecorder
